import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  Signal,
} from '@angular/core';
import { TsBadgeModule, TsButtonModule, TsInputComponent, TsPageHeaderModule } from 'tsui';
import { AssetManagementGridService } from '../../services/asset-management-grid.service';
import { ReportPeriodSelectorComponent } from '../report-period-selector/report-period-selector.component';
import { AssetManagementFileSources, AssetBudget, ReportPeriod } from '../../models';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { environment } from '@env/environment';

@Component({
  selector: 'budget-grid',
  standalone: true,
  imports: [
    TsBadgeModule,
    TsButtonModule,
    TsPageHeaderModule,
    ReportPeriodSelectorComponent,
    TsTableComponent,
    TsInputComponent,
    MatMenuModule,
    TitleCasePipe,
    NgTemplateOutlet,
  ],
  templateUrl: './budget-grid.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  styles: [
    `
      :host {
        @apply w-full h-full;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetGridComponent {
  readonly propertyBudget = input.required<AssetBudget>();
  readonly reportPeriod = input.required<ReportPeriod>();
  readonly loading = input.required<boolean>();
  readonly propertyBudgetColDefs: Signal<ColumnDef[]> = computed(() => {
    const listOfPeriodsInRange: ColumnDef[] =
      this.propertyBudget()?.report_period_range?.map((period) => ({
        label: BudgetGridComponent.formatPeriodLabel(period),
        // there's something wrong with ts-table type 'money' so i'm using 'text' for now
        type: 'text',
        name: `periods.${period}.amount`,
        meta: { readonly: true, scale: 2 },
        tsTableOptions: {
          headerName: period,
          valueFormatter: (params) => {
            return this.assetManagementGridService.formatCurrencyToAccounting(params.value);
          },
        },
      })) || [];

    return [{ ...this.accountColumnDef }, ...listOfPeriodsInRange];
  });
  readonly budgetFormFilter = new FormControl('');
  readonly budgetFormFilterChanged = toSignal(this.budgetFormFilter.valueChanges.pipe(startWith('')));
  readonly filteredBudgetRowData = computed(() => {
    if (this.propertyBudget() && this.budgetFormFilterChanged().length > 0) {
      return this.propertyBudget().budget.filter((entry) => {
        return entry.name.toLowerCase().includes(this.budgetFormFilter.value.toLowerCase());
      });
    } else {
      return this.propertyBudget()?.budget;
    }
  });

  @Output() reportPeriodChanged = new EventEmitter<ReportPeriod>();
  @Output() importBudget = new EventEmitter<{ file: File; source: AssetManagementFileSources }>();

  readonly assetManagementGridService = inject(AssetManagementGridService);

  get enableImport(): boolean {
    return environment?.env !== 'productionUI';
  }

  readonly accountColumnDef: ColumnDef = AssetManagementGridService.commonAccountColumnDef;

  private static get periodToMonthMap(): { [key: string]: string } {
    return {
      '1': 'January',
      '2': 'February',
      '3': 'March',
      '4': 'April',
      '5': 'May',
      '6': 'June',
      '7': 'July',
      '8': 'August',
      '9': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    };
  }

  private static formatPeriodLabel(period: string) {
    const [month, year] = period.split('/');

    return `${BudgetGridComponent.periodToMonthMap[month]} ${year}`;
  }
}
